import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { SOURCE_GTM2AMP, TagManager } from '~/src/common/services/TagManager';
import Tracker, { CommonCatalogEventProperties } from '~/src/common/services/Tracker';
import { Cart } from '~/src/common/typings/cart';
import { ProductDetailed } from '~/src/common/typings/product';
import { getPromoPercentage } from '~/src/common/utils/prices';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';

type QueryStringParams = {
  slug: string;
  fromList: string;
  product_source?: string;
  category?: string;
  subcategory?: string;
  eventSource?: string;
};

export const useProductContentAnalytics = (
  product: Nullable<ProductDetailed>,
  slug: string,
  analyticsProperties: CommonCatalogEventProperties,
) => {
  const { data: cart } = useGetCart<Cart>();
  const { query } = useRouter<QueryStringParams>();
  const { fromList, product_source, category, subcategory, eventSource } = query;

  useEffect(() => {
    if (product?.slug !== slug) return;

    const source = product_source ?? fromList;
    const productEventSource = SOURCE_GTM2AMP[source] ?? eventSource ?? 'products list';

    const isFromXSell = ['checkout cross sell', 'main product cross sell'].includes(
      productEventSource,
    );

    TagManager.pageView('ProductPage');
    TagManager.productView({ product, list: source });
    TagManager.productDetails({ product, list: source });

    const categoryName = isFromXSell ? 'Cross Selling' : category;

    Tracker.sendEvent('product page viewed', {
      'is page viewed': true,
      'product name': product.name,
      'product id': product.canonicalId,
      'product category': (categoryName ?? product.breadcrumbs?.[0]?.name) || '',
      'product subcategory': (subcategory ?? product.breadcrumbs?.[1]?.name) || '',
      'product pim': product.pimCategoryName,
      'product price': product.itemPrice / 100,
      'product event source': productEventSource,
      'product stock status': product.availableQuantity > 0 ? 'in stock' : 'out of stock',
      'product rating': product.rating?.average,
      'nb of ratings': product.rating?.nbRatings,
      'cart id': cart?.id ?? undefined,
      'promo percent':
        product.promo != null ? getPromoPercentage(product.promo, product.itemPrice) : undefined,
      'promo mechanism': product.promo?.mechanism,
      'promo triggering quantity': product.promo?.conditions?.nthQuantity,
      'current page name': slug,
      ...analyticsProperties,
    });
  }, [
    cart?.id,
    category,
    eventSource,
    fromList,
    product,
    product_source,
    slug,
    subcategory,
    analyticsProperties,
  ]);
};
